import config from '../../config';
import { Api, ApiConfig } from '../api';
import type { CloudAuditRun } from '../agentRunLedgerService';
import { PLUGIN_PRODUCT_CODE, withPluginProductHeaders } from '../productContext';

export interface PluginAuditRun extends CloudAuditRun {
  summaryContentState?: string;
  timelineTruncated?: boolean;
}

export interface PluginAuditRunListResponse {
  productCode: typeof PLUGIN_PRODUCT_CODE;
  workspaceId: string;
  limit: number;
  hasMore: boolean;
  nextCursor?: string | null;
  runs: PluginAuditRun[];
}

export type PluginAuditSummaryState =
  | 'stored'
  | 'redacted'
  | 'omitted'
  | 'absent'
  | 'legacy_unclassified';

export interface PluginAuditSummaryField {
  version: string;
  state: PluginAuditSummaryState;
  value?: string | null;
  truncated: boolean;
  omittedReason?: string | null;
}

export interface PluginAuditTaskSummaryResponse {
  productCode: typeof PLUGIN_PRODUCT_CODE;
  workspaceId: string;
  runId: string;
  startedAt?: string;
  completedAt?: string;
  goal: PluginAuditSummaryField;
  outcome: PluginAuditSummaryField;
  accessAuditId: string;
}

export interface PluginAuditListOptions {
  actorUserId?: string;
  applications?: string[];
  cursor?: string;
  endTime?: string;
  limit?: number;
  query?: string;
  startTime?: string;
}

export class PluginAuditProductMismatchError extends Error {
    constructor() {
        super('Plugin audit response product mismatch');
        this.name = 'PluginAuditProductMismatchError';
    }
}

const workspaceHeaders = (workspaceId: string) => withPluginProductHeaders({headers: { 'X-Workspace-Id': workspaceId },});

const runCollectionPath = (workspaceId: string) => (
    `/api/products/plugin/workspaces/${encodeURIComponent(workspaceId)}/audit-log/runs`
);

const assertPluginResponse = <T extends { productCode: string }>(response: T): T => {
    if (response.productCode !== PLUGIN_PRODUCT_CODE) {
        throw new PluginAuditProductMismatchError();
    }
    return response;
};

/** Account-server-only client for the isolated BizSidekick audit projection. */
export class PluginAuditLogService {
    private api: Api;

    constructor(apiConfig: ApiConfig) {
        this.api = new Api(apiConfig);
    }

    async listRuns(
        workspaceId: string,
        options: PluginAuditListOptions = {},
    ): Promise<PluginAuditRunListResponse> {
        const response = await this.api.get<PluginAuditRunListResponse>(runCollectionPath(workspaceId), {
            params: {
                limit: options.limit ?? 10,
                ...(options.cursor ? { cursor: options.cursor } : {}),
                ...(options.startTime ? { start_time: options.startTime } : {}),
                ...(options.endTime ? { end_time: options.endTime } : {}),
                ...(options.query ? { query: options.query } : {}),
                ...(options.applications?.length ? { application: options.applications } : {}),
                ...(options.actorUserId ? { actor_user_id: options.actorUserId } : {}),
            },
            ...workspaceHeaders(workspaceId),
        });
        return assertPluginResponse(response);
    }

    async getTaskSummary(
        workspaceId: string,
        runId: string,
    ): Promise<PluginAuditTaskSummaryResponse> {
        const response = await this.api.get<PluginAuditTaskSummaryResponse>(
            `${runCollectionPath(workspaceId)}/${encodeURIComponent(runId)}/summary`,
            workspaceHeaders(workspaceId),
        );
        return assertPluginResponse(response);
    }
}

export const pluginAuditLogService = new PluginAuditLogService({
    baseURL: config.services.auth.apiHost,
    headers: { 'Content-Type': 'application/json' },
});
